import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        accent: "bg-accent/90 text-accent-foreground backdrop-blur-sm shadow-[0_0_20px_hsl(var(--neon-accent)/0.3)] hover:shadow-[0_0_35px_hsl(var(--neon-accent)/0.5),0_0_70px_hsl(var(--neon-accent)/0.2)] transition-all duration-300 border border-transparent",
        "hero-primary": [
          "relative overflow-hidden backdrop-blur-sm",
          "text-accent-foreground text-base font-semibold px-9 py-3.5 h-auto rounded-lg",
          "border border-transparent",
          "shadow-[0_0_30px_hsl(var(--neon-accent)/0.35),0_0_80px_hsl(var(--neon-accent)/0.12)]",
          "hover:shadow-[0_0_45px_hsl(var(--neon-accent)/0.55),0_0_120px_hsl(var(--neon-accent)/0.2)]",
          "hover:scale-[1.03] active:scale-[0.97]",
          "transition-all duration-300",
          "before:absolute before:inset-0 before:rounded-lg before:bg-gradient-to-r before:from-accent before:via-[hsl(var(--neon-teal))] before:to-accent before:bg-[length:200%_100%] before:animate-[shimmer_4s_ease-in-out_infinite_alternate]",
          "after:absolute after:inset-[1px] after:rounded-[7px] after:bg-accent/85 after:backdrop-blur-sm",
          "[&>*]:relative [&>*]:z-10",
        ].join(" "),
        "hero-secondary": "border border-border/30 bg-card/20 backdrop-blur-sm text-foreground hover:border-accent/30 hover:bg-card/40 hover:shadow-[0_0_20px_hsl(var(--neon-accent)/0.1)] text-base px-8 py-3 h-auto transition-all duration-300",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
